import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

import { MatOption } from '@angular/material/autocomplete';
import { map } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { FormInputComponent, ObjectType, FieldValueInterface } from '../../../../tokens-and-interfaces';
import { InlineEditService } from '../../inline-edit.service';
import { CrmPipelineService } from '../../../../shared-services';
import { UpdaterService } from '../updater.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  templateUrl: './pipeline-selector.component.html',
  styleUrls: ['../renderers.scss'],
  imports: [CommonModule, MatSelectModule, GalaxyFormFieldModule, TranslateModule, MatOption, ReactiveFormsModule],
})
export class PipelineSelectorComponent extends FormInputComponent {
  private readonly inlineEditService = inject(InlineEditService);
  private readonly pipelineService = inject(CrmPipelineService);
  private readonly updaterService = inject(UpdaterService);

  override objectType: ObjectType = 'Opportunity';
  _fieldId = '';
  set fieldId(fieldId: string) {
    this._fieldId = fieldId;
  }
  get fieldId(): string {
    return this._fieldId;
  }
  _control: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  set control(control: FormControl<string>) {
    this._control = control;
    this.control.setValidators(Validators.required);
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  _label = '';
  set label(label: string) {
    this._label = label;
  }
  get label(): string {
    return this._label;
  }

  protected readonly availableOptions$ = this.pipelineService.listPipelines$(this.objectType).pipe(
    map((pipelines) =>
      pipelines.map((pipeline) => {
        return { id: pipeline.id, name: pipeline.name };
      }),
    ),
  );

  constructor() {
    super();
    this.availableOptions$.pipe(takeUntilDestroyed()).subscribe((pipelines) => {
      const currentPipelineId = this.control.value;
      const pipelineFound = pipelines.find((p) => p.id === currentPipelineId);
      if (currentPipelineId && !pipelineFound) {
        this.control.setValue('');
        this.control.markAsTouched();
      }
      const onlyOnePipelineAccessible = pipelines.length === 1;
      if (onlyOnePipelineAccessible) {
        this.control.setValue(pipelines[0].id);
        this.control.markAsDirty();
        this.updaterService.updateStringField(this.fieldId, this.control.value);
      }
    });
  }

  set formGroup(group: UntypedFormGroup) {
    // do nothing
  }

  get hasModification(): boolean {
    return this.control.dirty;
  }

  get value(): FieldValueInterface {
    return {
      fieldId: this.fieldId,
      stringValue: this.control.value,
    };
  }

  applyPipelineMutations(): void {
    this.updaterService.updateStringField(this.fieldId, this.control.value);
  }

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, [
      {
        fieldId: this.fieldId,
        stringValue: this.control.value,
      },
    ]);
  }
}
